import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

export const CONSENT_KEY = "goyt-cookie-consent";
export const CONSENT_EVENT = "goyt-consent-changed";
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const { choice, ts } = JSON.parse(raw);
    if (Date.now() - ts > ONE_YEAR_MS) return null;
    return choice;
  } catch {
    return null;
  }
};

export const setConsent = (choice) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, ts: Date.now() }));
  window.dispatchEvent(new Event(CONSENT_EVENT));
};

export const useConsent = () => {
  const [consent, setConsentState] = useState(getConsent);
  useEffect(() => {
    const onChange = () => setConsentState(getConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);
  return consent;
};

export const CookieConsent = () => {
  const consent = useConsent();
  const reduceMotion = useReducedMotion();
  const visible = consent === null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-[9999] bg-[#FCF5EB]/95 backdrop-blur-md border-t border-[#B4A893]/40"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm font-light text-[#3F6B72] text-center md:text-left leading-relaxed">
              A few cookies, nothing more. The booking calendar uses them to
              work — we don't track you.
            </p>
            <div className="flex items-center gap-4 shrink-0">
              <button
                onClick={() => setConsent("declined")}
                className="text-xs tracking-[0.2em] uppercase text-[#8F8375] hover:text-[#02515A] transition-colors duration-300"
                data-testid="cookie-decline-button"
              >
                Decline
              </button>
              <button
                onClick={() => setConsent("accepted")}
                className="border border-[#02515A] px-8 py-3 text-xs tracking-[0.2em] uppercase text-[#02515A] hover:bg-[#02515A] hover:text-[#FCF5EB] transition-colors duration-500"
                data-testid="cookie-accept-button"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
